import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  Business,
  Payment,
  Warning,
  SwapHoriz
} from '@mui/icons-material';
import { useProfile } from '../../context/userContext';
import { useAuth } from '../../context/authContext';
import styles from '../../styles/IndividualToOrgUpgrade.module.css';

const UpgradeConfirmation = ({
  migrationData,
  organizationData,
  selectedPlan,
  billingInterval,
  currentSubscription,
  onBack
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const { profile } = useProfile();
  const { token } = useAuth();

  const selectedPrice = selectedPlan?.prices?.find(
    p => p.recurring?.interval === billingInterval
  );

  const totalItems =
    migrationData.drones.length +
    migrationData.missions.length +
    migrationData.operations.length +
    migrationData.files.length;

  const formatPrice = (amount, interval) => {
    if (!amount) return 'Custom';
    const price = interval === 'year' ? amount / 12 / 100 : amount / 100;
    return `$${Math.round(price)}`;
  };

  const formatTotalPrice = (amount, interval) => {
    if (!amount) return 'Custom';
    const total = amount / 100;
    return `$${total.toFixed(2)}`;
  };

  const handleProceedToPayment = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create upgrade checkout session
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription/upgrade-to-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceId: selectedPrice.id,
          organizationData: {
            name: organizationData.name,
            identifier: organizationData.identifier,
            type: organizationData.type,
            teamSize: organizationData.teamSize
          },
          migrationData: {
            drones: migrationData.drones,
            missions: migrationData.missions,
            operations: migrationData.operations,
            files: migrationData.files,
            settings: migrationData.settings
          },
          currentSubscriptionId: currentSubscription?.stripeSubscriptionId,
          customerId: profile.userId,
          email: profile.email,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create upgrade session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      setError('Unable to proceed to payment. Please try again or contact support.');
      setProcessing(false);
    }
  };

  return (
    <div className={styles['upgrade-confirmation']}>
      <div className={styles['form-section']}>
        <h2 className={styles['section-title']}>
          Review Your Upgrade
        </h2>
        <p className={styles['section-description']}>
          Please review all details before proceeding to payment
        </p>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} className={styles['error-alert']}>
            {error}
          </Alert>
        )}

        {/* Migration Summary */}
        <Card className={styles['summary-card']} elevation={2}>
          <CardContent>
            <div className={styles['summary-header']}>
              <SwapHoriz className={styles['summary-icon']} />
              <h3 className={styles['summary-title']}>
                Data Migration
              </h3>
            </div>

            <div className={styles['migration-stats']}>
              <div className={styles['stat-row']}>
                <span className={styles['stat-label']}>Drones:</span>
                <Chip
                  label={`${migrationData.drones.length} items`}
                  size="small"
                  color={migrationData.drones.length > 0 ? 'primary' : 'default'}
                />
              </div>
              <div className={styles['stat-row']}>
                <span className={styles['stat-label']}>Missions:</span>
                <Chip
                  label={`${migrationData.missions.length} items`}
                  size="small"
                  color={migrationData.missions.length > 0 ? 'primary' : 'default'}
                />
              </div>
              <div className={styles['stat-row']}>
                <span className={styles['stat-label']}>Flight Operations:</span>
                <Chip
                  label={`${migrationData.operations.length} items`}
                  size="small"
                  color={migrationData.operations.length > 0 ? 'primary' : 'default'}
                />
              </div>
              <div className={styles['stat-row']}>
                <span className={styles['stat-label']}>Files:</span>
                <Chip
                  label={`${migrationData.files.length} items`}
                  size="small"
                  color={migrationData.files.length > 0 ? 'primary' : 'default'}
                />
              </div>
              <div className={styles['stat-row']}>
                <span className={styles['stat-label']}>Account Settings:</span>
                <Chip
                  label={migrationData.settings ? 'Included' : 'Not included'}
                  size="small"
                  color={migrationData.settings ? 'primary' : 'default'}
                />
              </div>
            </div>

            <Divider className={styles['divider']} />

            <div className={styles['migration-total']}>
              <strong>Total Items to Migrate:</strong> {totalItems}
            </div>
          </CardContent>
        </Card>

        {/* Organization Details */}
        <Card className={styles['summary-card']} elevation={2}>
          <CardContent>
            <div className={styles['summary-header']}>
              <Business className={styles['summary-icon']} />
              <h3 className={styles['summary-title']}>
                Organization Details
              </h3>
            </div>

            <div className={styles['summary-content']}>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Organization Name:</span>
                <span className={styles['summary-value']}>{organizationData.name}</span>
              </div>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Identifier:</span>
                <span className={styles['summary-value']}>{organizationData.identifier}</span>
              </div>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Type:</span>
                <span className={styles['summary-value']}>
                  {organizationData.type.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan Summary */}
        <Card className={styles['summary-card']} elevation={2}>
          <CardContent>
            <div className={styles['summary-header']}>
              <Payment className={styles['summary-icon']} />
              <h3 className={styles['summary-title']}>
                Selected Plan
              </h3>
            </div>

            <div className={styles['plan-summary']}>
              <div className={styles['plan-summary-header']}>
                <h4 className={styles['plan-summary-name']}>{selectedPlan?.name}</h4>
                <div className={styles['plan-summary-price']}>
                  <span className={styles['price-amount']}>
                    {formatPrice(selectedPrice?.unit_amount, billingInterval)}
                  </span>
                  <span className={styles['price-period']}>/month</span>
                </div>
              </div>

              <Divider className={styles['divider']} />

              <div className={styles['billing-summary']}>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>Billing Frequency:</span>
                  <span className={styles['summary-value']}>
                    {billingInterval === 'month' ? 'Monthly' : 'Annual'}
                  </span>
                </div>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>
                    {billingInterval === 'month' ? 'Monthly Total:' : 'Annual Total:'}
                  </span>
                  <span className={styles['summary-value-highlight']}>
                    {formatTotalPrice(selectedPrice?.unit_amount, billingInterval)}
                    {billingInterval === 'year' && '/year'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Subscription Notice */}
        {currentSubscription && (
          <Alert severity="warning" icon={<Warning />} className={styles['subscription-notice']}>
            <strong>Current Individual Subscription:</strong>
            <br />
            Your {currentSubscription.name} plan will be canceled as part of this upgrade.
            You'll be refunded for any unused time on your current billing cycle.
          </Alert>
        )}

        {/* Trial Notice */}
        <Alert severity="info" className={styles['trial-notice']}>
          <strong>7-Day Free Trial:</strong> Your organization trial starts today.
          You won't be charged until after the trial period ends. Cancel anytime during
          the trial with no charge.
        </Alert>

        {/* Terms and Conditions */}
        <div className={styles['terms-container']}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
                inputProps={{
                  'aria-label': 'Accept Terms of Service and Privacy Policy'
                }}
              />
            }
            label={
              <span className={styles['terms-label']}>
                I understand that my individual subscription will be canceled, and I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['terms-link']}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['terms-link']}
                >
                  Privacy Policy
                </a>
              </span>
            }
          />
        </div>
      </div>

      <Box className={styles['form-actions']}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={processing}
          aria-label="Go back to plan selection"
          className={styles['btn-secondary']}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleProceedToPayment}
          disabled={!acceptedTerms || processing}
          aria-label="Proceed to secure payment and complete upgrade"
          className={styles['btn-primary']}
        >
          {processing ? 'Processing...' : 'Complete Upgrade'}
        </Button>
      </Box>
    </div>
  );
};

export default UpgradeConfirmation;
